import { WarningAlt } from "@carbon/icons-react";
import {
	type DateRangePickerStateOptions,
	useDateRangePickerState,
} from "@react-stately/datepicker";
import cx from "classnames";
import { type FC, useCallback, useRef } from "react";
import { type DateValue, useDateRangePicker } from "react-aria";

import { RangeCalendar } from "../Calendar/RangeCalendar";
import { type InputDateSize } from "../InputDate";
import { makePointerStyles, makeTextStyles } from "../utilities";
import { CalendarIconButton } from "./CalendarIconButton";
import { CalendarPopover } from "./CalendarPopover";
import { CalendarPopoverDialog } from "./CalendarPopoverDialog";
import { InputDateBare } from "./InputDateBare";
import { datePickerGroupClassname } from "./common";

export type BaseDateRangePickerProps = Omit<
	DateRangePickerStateOptions<DateValue>,
	"isDisabled"
> & {
	size?: InputDateSize;
	disabled?: boolean;
	visibleDurationMonths?: 1 | 2;
	ranged: true;
	type?: "standalone" | "embedded";
	dialogTrigger?: "manual" | "click";
	hideTriggerButton?: boolean;
	withPortal?: boolean;
	constantWidthDateSegments?: boolean;
};

export const BaseDateRangePicker: FC<BaseDateRangePickerProps> = ({
	size = "sm",
	type = "standalone",
	dialogTrigger = "manual",
	hideTriggerButton = false,
	disabled = false,
	visibleDurationMonths = 1,
	withPortal = true,
	constantWidthDateSegments = true,
	...props
}) => {
	props = Object.assign(props, { isDisabled: disabled });

	const state = useDateRangePickerState(props);
	const ref = useRef<HTMLDivElement>(null);
	const {
		groupProps,
		labelProps,
		startFieldProps,
		endFieldProps,
		buttonProps,
		dialogProps,
		calendarProps,
	} = useDateRangePicker(props, state, ref);

	const onClickSegment = useCallback(() => {
		if (dialogTrigger === "click") {
			state.open();
		}
	}, [dialogTrigger, state]);

	return (
		<div className="relative inline-flex flex-col space-y-1 text-left">
			{props.label && (
				<span
					{...labelProps}
					className={cx(makeTextStyles(disabled, size), makePointerStyles(disabled))}
				>
					{props.label}
				</span>
			)}
			<div
				{...groupProps}
				ref={ref}
				className={cx(
					makePointerStyles(disabled),
					datePickerGroupClassname(disabled, size, type)
				)}
			>
				<div className="relative flex items-center">
					<InputDateBare
						datePickerDialogTrigger={dialogTrigger}
						triggerDatePickerDialog={onClickSegment}
						constantWidthDateSegments={constantWidthDateSegments}
						{...startFieldProps}
					/>
					<span aria-hidden="true" className="px-2">
						–
					</span>
					<InputDateBare
						datePickerDialogTrigger={dialogTrigger}
						triggerDatePickerDialog={onClickSegment}
						constantWidthDateSegments={constantWidthDateSegments}
						{...endFieldProps}
					/>
					{state.validationState === "invalid" && (
						<WarningAlt className="absolute right-1 h-6 w-6 text-negative" />
					)}
				</div>
				{!hideTriggerButton && (
					<CalendarIconButton
						size={size}
						{...buttonProps}
						isPressed={state.isOpen}
						disabled={disabled}
					/>
				)}
			</div>
			{state.isOpen && (
				<CalendarPopover
					triggerRef={ref}
					state={state}
					placement="bottom start"
					withPortal={withPortal}
				>
					<CalendarPopoverDialog {...dialogProps}>
						<RangeCalendar
							visibleDurationMonths={visibleDurationMonths}
							{...calendarProps}
						/>
					</CalendarPopoverDialog>
				</CalendarPopover>
			)}
		</div>
	);
};
